/**
 * §11 — **panggilan tamu** di HP pelayan. Inilah bagian yang membuat QR di meja ada gunanya.
 *
 * ### ⭐ Push (K5) — dan layar inilah yang MEMBUKTIKAN kanalnya
 * Dulu di sini ada poll 5 detik, dengan catatan "saat push tiba, yang berubah hanya sumber data di
 * berkas ini". Push sudah tiba, dan catatan itu ternyata benar: yang berganti cuma pemicu `load()`.
 *
 * ⚠️ Layar ini sengaja dipilih sebagai penumpang pertama kanal — fiturnya sudah jadi dan sudah
 * diuji, jadi kalau ada yang rusak, yang rusak pasti kanalnya. KDS menyusul di atas kanal yang
 * sudah terbukti, bukan di atas dua hal baru sekaligus.
 *
 * ### ⛔ Kalau kanalnya jatuh, pelayan HARUS tahu
 * Jaring poll tetap jalan saat kanal mati, tapi ia **tak dibiarkan menyamar jadi sehat**: lencana
 * `guest-degraded` muncul. Push yang diam-diam berubah jadi poll lambat berarti pelayan memercayai
 * layar yang tak lagi memberitahunya apa-apa.
 *
 * ### ⛔ Bunyi WAJIB dinyalakan lewat sentuhan
 * Browser HP menolak memutar audio yang tak berasal dari gestur pengguna — diam-diam, tanpa galat.
 * Itu bentuk kegagalan terburuk untuk fitur ini: pelayan mengira sistem menjaganya, padahal HP-nya
 * bisu sepanjang shift. Karena itu keadaan bunyi **ditampilkan terus-menerus**, bukan diasumsikan.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, type GuestCall } from '../api/client';
import { openLive, type LiveState } from '../api/live';
import { staffSession } from '../api/session';

export interface GuestCallPanelProps {
  readonly outletCode: string;
  /** Area yang dilayani pelayan ini. `undefined` = semua area. */
  readonly areaId?: string;
}

/**
 * Bel dua nada dari WebAudio — **tanpa berkas audio**.
 *
 * ⭐ Berkas suara berarti satu permintaan jaringan lagi di wifi restoran yang buruk, dan bunyi yang
 * gagal diunduh adalah bunyi yang tak berbunyi. Osilator selalu ada.
 */
function makeBell(): { playSound: () => void; ready: () => Promise<void> } {
  let ctx: AudioContext | null = null;
  const ambil = (): AudioContext => {
    ctx ??= new AudioContext();
    return ctx;
  };
  return {
    async ready() {
      const c = ambil();
      // ⚠️ Dipanggil DARI dalam handler sentuhan — di luar itu, `resume()` diabaikan browser HP.
      if (c.state === 'suspended') await c.resume();
    },
    playSound() {
      const c = ambil();
      if (c.state !== 'running') return;
      const startedAt = c.currentTime;
      for (const [i, freq] of [880, 1175].entries()) {
        const osc = c.createOscillator();
        const gain = c.createGain();
        osc.frequency.value = freq;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.0001, startedAt + i * 0.18);
        gain.gain.exponentialRampToValueAtTime(0.35, startedAt + i * 0.18 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, startedAt + i * 0.18 + 0.16);
        osc.connect(gain).connect(c.destination);
        osc.start(startedAt + i * 0.18);
        osc.stop(startedAt + i * 0.18 + 0.18);
      }
    },
  };
}

export function GuestCallPanel(p: GuestCallPanelProps) {
  const { t } = useTranslation();
  const [calls, setCalls] = useState<readonly GuestCall[]>([]);
  const [failed, setFailed] = useState(false);
  const [soundOn, setSoundOn] = useState(false);
  const [suspended, setDitangguhkan] = useState<string | null>(null);
  const [liveState, setLiveState] = useState<LiveState>('connecting');
  const bell = useRef(makeBell());
  /** ⭐ Yang sudah pernah dibunyikan — supaya panggilan lama tak berbunyi tiap 5 detik. */
  const everPlayed = useRef<Set<string>>(new Set());

  const load = useCallback(async () => {
    try {
      const list = await api.guestCalls(p.outletCode, p.areaId);
      setFailed(false);
      const fresh = list.filter((c) => !everPlayed.current.has(c.id));
      for (const c of list) everPlayed.current.add(c.id);
      if (fresh.length > 0) bell.current.playSound();
      setCalls(list);
    } catch {
      /*
       * ⚠️ Kegagalan muat **tidak mengosongkan daftar**. Layar kosong berarti "tak ada yang
       * memanggil" bagi pelayan — dan itu kebohongan yang persis kebalikan dari keadaannya.
       */
      setFailed(true);
    }
  }, [p.outletCode, p.areaId]);

  useEffect(() => {
    /*
     * ⛔ Muat pertama SELALU langsung, tak menunggu kanal. Menunda isi layar sampai `ready` datang
     * berarti pelayan menatap daftar kosong selama jabat tangan WebSocket — dan kosong berarti
     * "tak ada yang memanggil", kebohongan yang persis kebalikan dari keadaannya.
     */
    void load();
    const staf = staffSession();
    if (staf === undefined) return;
    const kanal = openLive({
      outletCode: p.outletCode,
      staffToken: staf.token,
      /*
       * ⚠️ Dentuman TAK membawa isi — ia cuma memicu pembacaan ulang ke endpoint yang sama dengan
       * saat layar dimuat. Itu yang menjaga satu sumber kebenaran; lihat `api/live.ts`.
       */
      onNudge: (kind) => { if (kind === undefined || kind === 'guest_call') void load(); },
      onState: setLiveState,
    });
    return () => { kanal.close(); };
  }, [load, p.outletCode]);

  async function enableSound() {
    await bell.current.ready();
    setSoundOn(true);
    bell.current.playSound();
  }

  /**
   * ⭐⭐ "Bukan tamu" — pelayan melihat meja itu kosong.
   *
   * ⛔ Ini satu-satunya cara sistem bisa membedakan tamu asli dari orang jahil: keduanya mengirim
   * permintaan identik dari HP identik lewat QR yang sama-sama sah. Yang membedakan hanya mata
   * pelayan di lantai. Sesudah cukup banyak, server menangguhkan QR meja itu sendiri.
   */
  async function notGuest(id: string, tableCode: string) {
    setCalls((k) => k.filter((c) => c.id !== id));
    try {
      const result = await api.spamGuestCall(id);
      if (result.suspendedUntil !== null) setDitangguhkan(tableCode);
    } finally {
      await load();
    }
  }

  async function accept(id: string) {
    // ⭐ Dihapus dari layar SEBELUM server menjawab: dua pelayan yang menekan bersamaan lebih baik
    // daripada satu meja yang tak didatangi karena tombolnya terasa mati.
    setCalls((k) => k.filter((c) => c.id !== id));
    try {
      await api.ackGuestCall(id);
    } finally {
      await load();
    }
  }

  return (
    <section className="guest-call" data-testid="guest-call-panel">
      <header className="guest-call-head">
        <h2>{t('guestCall.title')}</h2>
        <button
          type="button"
          data-testid="sound"
          className={soundOn ? 'sound on' : 'sound off'}
          onClick={() => { void enableSound(); }}
        >
          {soundOn ? t('guestCall.soundOn') : t('guestCall.enableSound')}
        </button>
      </header>

      {failed ? <p className="error" data-testid="guest-call-error">{t('guestCall.loadFailed')}</p> : null}
      {/*
        ⛔⛔ Kemunduran DIUMUMKAN, tak ditelan. Kanal yang mati lalu diam-diam diganti poll membuat
        pelayan memercayai layar yang sudah lambat berkali-kali lipat — dan yang pertama menyadari
        ada yang salah jadi tamu yang menunggu terlalu lama, bukan orang yang bisa memperbaikinya.
      */}
      {liveState === 'degraded' ? (
        <p className="warn" data-testid="guest-degraded">{t('guestCall.degraded')}</p>
      ) : null}

      {/* ⚠️ Diberitahukan ke PELAYAN, tak pernah ke tamu — si iseng tak boleh tahu mana yang berhasil. */}
      {suspended === null ? null : (
        <p className="guest-call-suspended" data-testid="guest-call-suspended">
          {t('guestCall.suspended', { table: suspended })}
        </p>
      )}

      {calls.length === 0 && !failed ? (
        <p className="empty" data-testid="guest-call-empty">{t('guestCall.empty')}</p>
      ) : null}

      <ul className="guest-call-list">
        {calls.map((c) => (
          <li key={c.id} className={`guest-call-item ${c.kind}`} data-testid={`guest-call-${c.tableCode}`}>
            <div className="guest-call-table">
              <strong>{c.tableCode}</strong>
              <span className="guest-call-area">{c.areaName}</span>
            </div>
            <div className="guest-call-body">
              <span className="guest-call-kind">{t(`guestCall.${c.kind}`)}</span>
              {c.note === null ? null : <span className="guest-call-note">{c.note}</span>}
              <span className="guest-call-wait">
                {c.waitingSeconds < 60
                  ? t('guestCall.waiting', { n: c.waitingSeconds })
                  : t('guestCall.waitingMinutes', { n: Math.floor(c.waitingSeconds / 60) })}
              </span>
            </div>
            <div className="guest-call-actions">
              <button type="button" onClick={() => { void accept(c.id); }}>
                {t('guestCall.accept')}
              </button>
              <button
                type="button"
                className="not-guest"
                data-testid={`not-guest-${c.tableCode}`}
                onClick={() => { void notGuest(c.id, c.tableCode); }}
              >
                {t('guestCall.notGuest')}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
